import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiCalendar, FiClock, FiUser, FiMail, FiVideo, FiXCircle, FiPlus, FiSettings, FiRefreshCw } from "react-icons/fi";
import { getBookings, cancelBooking } from "../../api/meetingsApi";
import toast from "react-hot-toast";

function formatDateTime(isoStr) {
  const d = new Date(isoStr);
  return {
    date: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
  };
}

function BookingCard({ booking, onCancel }) {
  const { date, time } = formatDateTime(booking.scheduled_at);
  const isPast = new Date(booking.scheduled_at) < new Date();

  return (
    <div className={`bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow ${isPast ? "opacity-60" : ""}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              booking.status === "confirmed" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
            }`}>
              {booking.status === "confirmed" ? "✓ Confirmed" : "✗ Cancelled"}
            </span>
            {isPast && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Past</span>}
          </div>

          <h3 className="font-semibold text-gray-800 text-base truncate">{booking.meeting_type_title}</h3>

          <div className="mt-3 space-y-1.5">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FiUser size={14} className="text-blue-500 shrink-0" />
              <span className="truncate">{booking.client_name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FiMail size={14} className="text-blue-500 shrink-0" />
              <span className="truncate">{booking.client_email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FiCalendar size={14} className="text-blue-500 shrink-0" />
              <span>{date}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FiClock size={14} className="text-blue-500 shrink-0" />
              <span>{time} · {booking.meeting_type_duration} min</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 items-end shrink-0">
          {booking.google_meet_link && (
            <a
              href={booking.google_meet_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition font-medium"
            >
              <FiVideo size={13} />
              Join Meet
            </a>
          )}
          {booking.status === "confirmed" && !isPast && (
            <button
              onClick={() => onCancel(booking.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-600 text-xs rounded-lg hover:bg-red-50 transition font-medium"
            >
              <FiXCircle size={13} />
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MeetingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("confirmed");

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBookings(tab);
      setBookings(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  const handleCancel = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this booking? The client will be notified.")) return;
    try {
      await cancelBooking(id);
      toast.success("Booking cancelled and client notified.");
      loadBookings();
    } catch (e) {
      toast.error(e.message || "Failed to cancel booking");
    }
  };

  const upcoming = bookings.filter(b => new Date(b.scheduled_at) >= new Date());
  const past     = bookings.filter(b => new Date(b.scheduled_at) <  new Date());

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Meetings</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your scheduled meetings and bookings</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/dashboard/meetings/types"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition font-medium"
          >
            <FiPlus size={15} />
            New Meeting Type
          </Link>
          <Link
            to="/dashboard/meetings/settings"
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition"
          >
            <FiSettings size={15} />
            Settings
          </Link>
          <button
            onClick={loadBookings}
            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-500"
            title="Refresh"
          >
            <FiRefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
        {["confirmed", "cancelled"].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm rounded-md font-medium transition capitalize ${
              tab === t ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-blue-600 border-r-transparent rounded-full animate-spin" />
        </div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
          <FiCalendar size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No {tab} meetings</p>
          <p className="text-sm text-gray-400 mt-1">
            {tab === "confirmed"
              ? "Create a meeting type to start accepting bookings."
              : "Cancelled meetings will appear here."}
          </p>
          {tab === "confirmed" && (
            <Link
              to="/dashboard/meetings/types"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
            >
              <FiPlus size={14} /> Create Meeting Type
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {tab === "confirmed" && upcoming.length > 0 && (
            <div>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Upcoming ({upcoming.length})</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {upcoming.map(b => <BookingCard key={b.id} booking={b} onCancel={handleCancel} />)}
              </div>
            </div>
          )}
          {tab === "confirmed" && past.length > 0 && (
            <div>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Past ({past.length})</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {past.map(b => <BookingCard key={b.id} booking={b} onCancel={handleCancel} />)}
              </div>
            </div>
          )}
          {tab === "cancelled" && (
            <div className="grid gap-4 sm:grid-cols-2">
              {bookings.map(b => <BookingCard key={b.id} booking={b} onCancel={handleCancel} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
